import React, { useState } from 'react';
import { Plus, Users, TrendingUp, DollarSign, Edit, UserX, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/helpers';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';


export default function Funcionarios() {
  const { data: funcionariosData, refresh: refreshFuncionarios } = useApiData('/advanced/funcionarios');
  const funcionarios = funcionariosData || [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cargo: '',
    tipo: 'fixo',
    custo_mensal: '',
    custo_por_hora: ''
  });
  const [loading, setLoading] = useState(false);
  const [editingFunc, setEditingFunc] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        custo_mensal: parseFloat(formData.custo_mensal),
        custo_por_hora: formData.custo_por_hora ? parseFloat(formData.custo_por_hora) : null
      };

      if (editingFunc) {
        await api.put(`/advanced/funcionarios/${editingFunc.id}`, payload);
        toast.success('Funcionário atualizado!');
      } else {
        await api.post('/advanced/funcionarios', payload);
        toast.success('Funcionário cadastrado!');
      }
      setIsDialogOpen(false);
      setEditingFunc(null);
      resetForm();
      refreshFuncionarios();

    } catch (error) {
      toast.error(editingFunc ? 'Erro ao atualizar funcionário' : 'Erro ao cadastrar funcionário');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cargo: '',
      tipo: 'fixo',
      custo_mensal: '',
      custo_por_hora: ''
    });
    setEditingFunc(null);
  };

  const openEdit = (func) => {
    setEditingFunc(func);
    setFormData({
      nome: func.nome,
      cargo: func.cargo,
      tipo: func.tipo,
      custo_mensal: String(func.custo_mensal),
      custo_por_hora: func.custo_por_hora ? String(func.custo_por_hora) : ''
    });
    setIsDialogOpen(true);
  };

  const toggleActive = async (func) => {
    try {
      await api.put(`/advanced/funcionarios/${func.id}`, { is_active: !func.is_active });
      toast.success(func.is_active ? 'Funcionário desativado' : 'Funcionário reativado');
      refreshFuncionarios();

    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const totalCustoEquipe = funcionarios
    .filter(f => f.is_active)
    .reduce((sum, f) => sum + f.custo_mensal, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Funcionários</h1>
          <p className="text-slate-600 mt-1">Gerencie sua equipe e custos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Novo Funcionário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingFunc ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
              <DialogDescription>{editingFunc ? 'Atualize os dados do funcionário' : 'Cadastre um novo membro da equipe'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Cargo *</Label>
                  <Input
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="Ex: Designer, Desenvolvedor"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixo">Fixo (CLT)</SelectItem>
                      <SelectItem value="freelancer">Freelancer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Custo Mensal (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.custo_mensal}
                    onChange={(e) => setFormData({ ...formData, custo_mensal: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Custo por Hora (R$) - Opcional</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.custo_por_hora}
                  onChange={(e) => setFormData({ ...formData, custo_por_hora: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Salvando...' : (editingFunc ? 'Atualizar' : 'Salvar')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
            <Users className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Total de Funcionários</p>
          <p className="text-3xl font-bold text-slate-900">{funcionarios.filter(f => f.is_active).length}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-error/10 flex items-center justify-center text-error mb-4">
            <DollarSign className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Custo Total da Equipe</p>
          <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalCustoEquipe)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-4">
            <TrendingUp className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Custo Médio</p>
          <p className="text-3xl font-bold text-slate-900">
            {formatCurrency(totalCustoEquipe / Math.max(funcionarios.filter(f => f.is_active).length, 1))}
          </p>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nome</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cargo</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Custo Mensal</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Custo/Hora</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {funcionarios.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-slate-500">
                    Nenhum funcionário cadastrado
                  </td>
                </tr>
              ) : (
                funcionarios.map((func) => (
                  <tr key={func.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="p-4 text-sm font-medium text-slate-900">{func.nome}</td>
                    <td className="p-4 text-sm text-slate-600">{func.cargo}</td>
                    <td className="p-4 text-sm">
                      <Badge variant={func.tipo === 'fixo' ? 'default' : 'secondary'}>
                        {func.tipo === 'fixo' ? 'CLT' : 'Freelancer'}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm font-mono text-slate-900">{formatCurrency(func.custo_mensal)}</td>
                    <td className="p-4 text-sm font-mono text-slate-600">
                      {func.custo_por_hora ? formatCurrency(func.custo_por_hora) : '-'}
                    </td>
                    <td className="p-4 text-sm">
                      <Badge variant={func.is_active ? 'default' : 'outline'}>
                        {func.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(func)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleActive(func)}
                          title={func.is_active ? 'Desativar' : 'Reativar'}
                        >
                          {func.is_active
                            ? <UserX className="h-4 w-4 text-red-500" />
                            : <UserCheck className="h-4 w-4 text-green-500" />
                          }
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}